import React, { useContext, useEffect, useState } from 'react'
import { TableColumnDefinition, makeStyles, Option, Field, Dropdown, Button, tokens, Divider, SearchBox } from '@fluentui/react-components'
import { t } from '../i18n/i18n'
import { List } from '../components/common/list/List'
import { Page } from './Page'
import { useSearchParams } from 'react-router-dom'
import { Grid } from '../components/common/grid/Grid'
import { GridItem } from '../components/common/grid/GridItem'
import { DataContext } from '../App'
import { useMsal } from '@azure/msal-react'
import { enumValuesToOptions, enumValuesToTranslate, matchingEnumKeys } from '../helpers/enumHelper'
import { getFilteredLaws, getLawColumns, onFilterReset, sortColumntOptions } from '../helpers/lawHelper'
import { Law, LawPaginatedList, LawStatus, StatusImport } from '../api/schemas/schema'
import { Title } from '../components/common/texts/Title'
import { ArrowClockwiseRegular } from '@fluentui/react-icons'
import { NoLawsFoundMessageBar } from '../components/law/NoLawsFoundMessageBar'
import { CounterBadge } from '../components/common/counterBadge/CounterBadge'
import { handleMessage, setLoading } from '../helpers/stateHelper'
import { UserMessage } from '../components/common/messageBar/UserMessage'
import { useDebouncedValue } from '../helpers/debounceHelper'
import { normalizeForSearch } from '../helpers/searchHelper'
import { Text } from '../components/common/texts/Text'

const MIN_SEARCH_LENGTH = 2
const SEARCH_DEBOUNCE_MS = 275

const useStyles = makeStyles({
   flexCenter: {
      display: 'flex',
      alignItems: 'center',
   },
   columnIconSize: {
      fontSize: tokens.fontSizeBase300,
      marginLeft: tokens.spacingVerticalSNudge,
   },
   cursor: {
      cursor: 'pointer',
   },
   flexEnd: {
      display: 'flex',
      justifyContent: 'end',
      gap: tokens.spacingHorizontalXL,
   },
   searchHint: {
      color: tokens.colorNeutralForeground3,
   },
   mt0: {
      marginTop: 0,
   },
   flexStart: {
      justifyContent: 'start',
   },
})
export const Laws = () => {
   const dataContext = useContext(DataContext)
   const { setRootState } = dataContext
   const classes = useStyles()
   const [columns, setColumns] = useState<TableColumnDefinition<Law>[]>([])
   const [statusFilter, setStatusFilter] = useState<LawStatus[]>([])
   const [importStatusFilter, setImportStatusFilter] = useState<StatusImport[]>([])
   const [columnSort, setColumnSort] = useState<string>('')
   const [searchParams, setSearchParams] = useSearchParams()
   const page = parseInt(searchParams.get('page'))
   const committedSearch = searchParams.get('search') ?? ''
   const { accounts } = useMsal()
   const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
   const pageSize = 100
   const [paginatedLawList, setPaginatedLawList] = useState<LawPaginatedList>({ pageIndex: 1, totalItems: undefined, totalPages: undefined, items: [] })
   const [fetchError, setFetchError] = useState(false)
   const [searchInput, setSearchInput] = useState<string>(committedSearch)
   const debouncedSearchInput = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS)
   const trimmedSearchInput = searchInput.trim()
   const searchTooShort = trimmedSearchInput.length > 0 && trimmedSearchInput.length < MIN_SEARCH_LENGTH
   const normalizedSearch = normalizeForSearch(committedSearch)
   const searchStatusMatches = matchingEnumKeys(LawStatus, normalizedSearch) as LawStatus[]
   const searchStatusImportMatches = matchingEnumKeys(StatusImport, normalizedSearch) as StatusImport[]
   const { totalItems, totalPages, items } = paginatedLawList
   const hasActiveFilter = importStatusFilter.length > 0 || statusFilter.length > 0 || columnSort !== '' || committedSearch !== ''
   const sortOn = columnSort !== '' ? columnSort : 'SfsNr'

   const commitSearch = (value: string) => {
      const trimmed = value.trim()
      setSearchParams((params) => {
         if ((params.get('search') ?? '') === trimmed) return params
         if (trimmed) params.set('search', trimmed)
         else params.delete('search')
         params.set('page', '1')
         return params
      })
   }
   const clearSearch = () => {
      setSearchInput('')
      commitSearch('')
   }

   useEffect(() => {
      const trimmed = debouncedSearchInput.trim()
      commitSearch(trimmed.length < MIN_SEARCH_LENGTH ? '' : trimmed)
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [debouncedSearchInput])

   useEffect(() => {
      setColumns(
         getLawColumns(
            dataContext,
            accounts[0],
            sortOn,
            sortOrder,
            classes,
            setPaginatedLawList,
            toggleSortOrder,
            statusFilter,
            importStatusFilter,
            page,
            pageSize,
            normalizedSearch
         )
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [columnSort, importStatusFilter, normalizedSearch, page, sortOn, sortOrder, statusFilter])
   const toggleSortOrder = () => {
      setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'))
   }
   useEffect(() => {
      if (!searchParams.get('page'))
         setSearchParams((params) => {
            params.set('page', '1')
            return params
         })
      else if (parseInt(searchParams.get('page')) > totalPages)
         setSearchParams((params) => {
            params.set('page', totalPages > 0 ? totalPages.toString() : '1')
            return params
         })
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [page, searchParams, setSearchParams])
   useEffect(() => {
      if (typeof page === 'number' && !isNaN(page)) {
         const controller = new AbortController()
         ;(async () => {
            const { setRootState } = dataContext
            setLoading(setRootState, true)
            const sortOn = columnSort !== '' ? columnSort : 'SfsNr'
            try {
               let result = await getFilteredLaws(
                  statusFilter,
                  importStatusFilter,
                  sortOn,
                  sortOrder,
                  page,
                  pageSize,
                  dataContext.state.accessToken,
                  committedSearch || undefined,
                  searchStatusMatches,
                  searchStatusImportMatches,
                  controller.signal
               )

               setPaginatedLawList(result)
               setFetchError(false)
            } catch (error: any) {
               if (error?.name !== 'AbortError') {
                  setFetchError(true)
                  handleMessage(setRootState, 'error', t('FetchFailed'), t('CouldNotFetchLaws'))
               }
            } finally {
               setLoading(setRootState, false)
            }
         })()
         return () => controller.abort()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [page, setRootState, statusFilter, importStatusFilter, columnSort, sortOrder, committedSearch])

   const handleStatusOptionSelect = (event: any, data: any) => {
      setStatusFilter(data.selectedOptions)

      setSearchParams((params) => {
         params.set('page', '1')
         return params
      })
   }
   const handleImportStatusOptionSelect = (event: any, data: any) => {
      setImportStatusFilter(data.selectedOptions)

      setSearchParams((params) => {
         params.set('page', '1')
         return params
      })
   }
   const enumStatusTranslation = (number: number): string => {
      if (number < 100) return enumValuesToTranslate(LawStatus, number)
      return enumValuesToTranslate(StatusImport, number)
   }

   return (
      <Page header={<Title as="h2">{t('LawsImport')}</Title>}>
         <Grid>
            <GridItem size="2/12">
               <Field>
                  <SearchBox
                     placeholder={t('SearchLaws')}
                     value={searchInput}
                     onChange={(e, data) => {
                        setSearchInput(data.value)
                        // The dismiss cross fires onChange empty; clear at once, not after the debounce.
                        if (!data.value) commitSearch('')
                     }}
                     onKeyDown={(e) => {
                        if (e.key === 'Enter' && trimmedSearchInput.length >= MIN_SEARCH_LENGTH) commitSearch(searchInput)
                        if (e.key === 'Escape') clearSearch()
                     }}
                  />
                  {searchTooShort && (
                     <Text as="caption" className={classes.searchHint}>
                        {t('SearchMinLengthHint')}
                     </Text>
                  )}
               </Field>
            </GridItem>
            <GridItem size="10/12" className={classes.flexEnd}>
               <Field>
                  <Dropdown
                     multiselect
                     placeholder={t('FilterOnProcessStatus')}
                     onOptionSelect={handleStatusOptionSelect}
                     selectedOptions={statusFilter.map((x) => x.toString())}
                     value={statusFilter ? statusFilter.map((x) => enumStatusTranslation(Number(x))).join(', ') : ''}
                  >
                     {enumValuesToOptions(LawStatus).map((option) => (
                        <Option key={option.key.toString()} value={option.key.toString()}>
                           {option.text}
                        </Option>
                     ))}
                  </Dropdown>
               </Field>
               <Field>
                  <Dropdown
                     multiselect
                     placeholder={t('FilterOnImportStatus')}
                     onOptionSelect={handleImportStatusOptionSelect}
                     selectedOptions={importStatusFilter.map((x) => x.toString())}
                     value={importStatusFilter ? importStatusFilter.map((x) => enumStatusTranslation(Number(x))).join(', ') : ''}
                  >
                     {enumValuesToOptions(StatusImport).map((option) => (
                        <Option key={option.key.toString()} value={option.key.toString()}>
                           {option.text}
                        </Option>
                     ))}
                  </Dropdown>
               </Field>
               <Field>
                  <Dropdown
                     key={columnSort}
                     placeholder={t('SortOnColumn')}
                     onOptionSelect={(e, selectedColumn) => {
                        setSortOrder('asc')
                        setColumnSort(selectedColumn.optionValue)
                     }}
                     value={columnSort !== '' ? sortColumntOptions.find((x) => x.value === columnSort).text : ''}
                     selectedOptions={[columnSort !== '' ? columnSort : 'SfsNr']}
                  >
                     {sortColumntOptions.map((option) => (
                        <Option key={option.value} value={option.value}>
                           {option.text}
                        </Option>
                     ))}
                  </Dropdown>
               </Field>
               <Button
                  icon={<ArrowClockwiseRegular />}
                  appearance="outline"
                  onClick={() => {
                     onFilterReset(setImportStatusFilter, setStatusFilter, setColumnSort)
                     clearSearch()
                  }}
                  disabled={!hasActiveFilter}
               >
                  {t('ResetFilter')}
               </Button>
               <CounterBadge number={totalItems} text={t(hasActiveFilter ? 'LawsWithFilter' : 'LawsWithoutFilter')} loading={dataContext.state.isLoading} />
            </GridItem>
            <GridItem size="12/12">
               <Title as="h3" className={classes.mt0}>
                  {t('ImportedLaws')}
               </Title>
               <Divider appearance="brand" />
            </GridItem>
            <GridItem size="12/12">
               {fetchError ? (
                  <UserMessage type="error" title="FetchFailed" message="CouldNotFetchLaws" />
               ) : (
                  <>
                     <List
                        pagination={items && items.length > 0 ? true : false}
                        numberOfPages={totalPages}
                        numberOfLaws={totalItems}
                        columns={columns}
                        items={items}
                        loading={dataContext.state.isLoading}
                        asCard
                        pageSize={pageSize}
                     />
                     {!dataContext.state.isLoading &&
                        items.length === 0 &&
                        (importStatusFilter.length > 0 || statusFilter.length > 0 || committedSearch !== '') && (
                           <NoLawsFoundMessageBar isSearching={committedSearch !== ''} onClearSearch={clearSearch} />
                        )}
                  </>
               )}
            </GridItem>
         </Grid>
      </Page>
   )
}
